import React, { useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import Question from "../../components/Question";
import MoveButton from "../../components/MoveButton";
import { Stack, Box, useTheme, useMediaQuery } from "@mui/material";
import { setAllPatientData, setPreviousCaseId } from '../../redux/userSlice';
import { setAllPatientInsuranceData } from "../../redux/insuranceSlice";
import dayjs from 'dayjs';
import { formatAssessmentText } from '../../lib/formatting';
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";

export default function PatientCases() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
    const location = useLocation();
    const { portal = false } = location.state || {};
    const patient = useSelector((state) => state.user.selectedReturningPatient);
    const disableAutoSelect = !!location.state?.disableAutoSelect;

    const handleCaseSelection = (selectedCase) => {
        const lower = patient?.fields?.Payer?.toLowerCase?.();
        const payer = lower.includes('insurance')
            ? 'insurance'
            : lower.includes("paying for myself") || lower.includes('self')
                ? 'self'
                : patient?.fields?.Payer;
        const patientData = {
            firstName: patient.first_name,
            lastName: patient.last_name,
            dob: dayjs(patient.date_of_birth, 'ddd, DD MMM YYYY HH:mm:ss [GMT]').toISOString(),
            address: {
                line1: patient.address_line_1,
                line2: patient.address_line_2,
                city: patient.city,
                state: patient.state,
                zip: patient.zipcode
            },
            sex: { sexAtBirth: patient.sex, maternity: patient.pregnancy_status, cycle: [] },
            phoneNo: patient.contact.phone,
            email: patient.contact.email,
            tellescopeID: patient.tellescope_patient_id,
            returningFullName: patient.full_name,
            patientID: patient.id,
            payMethod: payer,
            isIsotretinoin: patient.fields.accutane === "Y"
        };

        const insuranceData = {
            insurer: patient.insurance?.payerName,
            memberID: patient.insurance?.memberId,
            policyOwner: {
                relationship: patient.insurance?.relationship,
                fname: patient.insurance?.relationshipDetails?.fname,
                lname: patient.insurance?.relationshipDetails?.lname,
                gender: patient.insurance?.relationshipDetails?.gender,
                dob: patient.insurance?.relationshipDetails?.dateOfBirth,
            },
            cardFront: patient.insurance?.cardFront,
            cardBack: patient.insurance?.cardBack,
        };

        dispatch(setAllPatientData(patientData));
        dispatch(setAllPatientInsuranceData(insuranceData));
        dispatch(setPreviousCaseId(selectedCase.id));

        setPageAndDeferNavigate(dispatch, '/feeling-now', () =>
            navigate('/feeling-now', { state: { portal } })
        );
    };

    const assessedCases = useMemo(
        () =>
            (patient?.cases ?? []).filter((c) => {
                const a = c?.assessment;
                if (a == null) return false;
                if (Array.isArray(a)) return a.length > 0;
                if (typeof a === 'object') return Object.keys(a).length > 0;
                return !!a;
            }),
        [patient?.cases]
    );

    useEffect(() => {
        if (disableAutoSelect) return;
        const allCases = patient?.cases ?? [];
        if (!allCases.length) return;

        if (assessedCases.length === 1) {
            handleCaseSelection(assessedCases[0]);
            return;
        }

        if (assessedCases.length === 0) {
            handleCaseSelection(allCases[0]);
            return;
        }

    }, [patient?.cases, assessedCases, disableAutoSelect]);


    const handleBack = () => {
        if (portal) {
            navigate('/returning-reason', { state: { portal } });
        } else {
            setPageAndDeferNavigate(dispatch, '/returning-reason', () =>
                navigate('/returning-reason', { state: { portal } })
            );
        }
    };

    if (!disableAutoSelect && assessedCases.length <= 1) return null;

    return (
        <Question
            onBack={handleBack}
            noBack={false}
            returning={true}
            title="Select a case"
            subtitle="Which condition are you following up on?"
            input={
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    width="100%"
                >
                    <Stack
                        spacing={2}
                        width="100%"
                        maxWidth="sm"
                        alignItems="center"
                    >
                        {assessedCases.map((caseItem, index) => {
                            const isLatest = index === 0;

                            const submittedLabel = caseItem?.created_at
                                ? new Date(`${caseItem.created_at}Z`).toLocaleDateString(undefined, {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                })
                                : null;

                            return (
                                <Box
                                    key={caseItem.id ?? index}
                                    sx={{
                                        position: 'relative',
                                        width: '100%',
                                        maxWidth: '400px',
                                    }}
                                >
                                    {/* timeline line */}
                                    {index !== assessedCases.length - 1 && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                left: 20,
                                                top: 42,
                                                bottom: -16,
                                                width: '1px',
                                                bgcolor: '#E2E2E2',
                                            }}
                                        />
                                    )}

                                    <MoveButton
                                        onClick={() => handleCaseSelection(caseItem)}
                                        title={
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={2}
                                                width="100%"
                                            >
                                                {/* timeline dot */}
                                                <Box
                                                    sx={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        bgcolor: isLatest ? '#2200BB' : '#D0D0D0',
                                                        flexShrink: 0,
                                                    }}
                                                />

                                                <Box sx={{ flex: 1, textAlign: 'left' }}>
                                                    <Box
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.95rem',
                                                        }}
                                                    >
                                                        {isLatest
                                                            ? 'Latest visit'
                                                            : `Visit ${assessedCases.length - index}`}
                                                    </Box>

                                                    <Box
                                                        sx={{
                                                            fontSize: '0.75rem',
                                                            color: 'text.secondary',
                                                            mt: 0.25,
                                                        }}
                                                    >
                                                        {submittedLabel} • {formatAssessmentText(caseItem?.assessment)}
                                                    </Box>
                                                </Box>
                                            </Stack>
                                        }
                                        sx={{
                                        width: isDesktop ? '100%' : '100%',
                                        maxWidth: '400px',
                                        }}
                                    />
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            }
        />
    );
}